import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Zap,
  RefreshCw,
  AlertTriangle,
  Volume2,
  Radio,
  History,
  Bot
} from 'lucide-react';
import {
  ScannerConfig,
  ScannerResult,
  ScannerColumn,
  ScannerSortConfig,
  ScannerMetadata,
  ScannerType
} from '@/services/scannerService';
import signalRService from '@/services/signalRService';
import backtestService from '@/services/backtestService';
import type { BacktestClockTick } from '@/services/backtestService';
import { automationService, type Strategy } from '@/services/strategyService';
import BacktestControlBar, { type BacktestState } from './BacktestControlBar';
import StrategySummaryPopover from './StrategySummaryPopover';
import DynamicScannerTable from './DynamicScannerTable';
import FirstRunChecklist from './FirstRunChecklist';

interface ScannerPanelProps {
  scanner: ScannerConfig;
  results: ScannerResult[];
  columns?: ScannerColumn[];
  sort?: ScannerSortConfig;
  onSymbolClick?: (symbol: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

// Format helpers
const formatPrice = (price: number): string => {
  return price.toFixed(2);
};

const formatVolume = (volume: number): string => {
  if (volume >= 1000000000) return `${(volume / 1000000000).toFixed(1)}B`;
  if (volume >= 1000000) return `${(volume / 1000000).toFixed(1)}M`;
  if (volume >= 1000) return `${(volume / 1000).toFixed(0)}K`;
  return volume.toString();
};

const formatPercent = (percent: number): string => {
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(2)}%`;
};

const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/New_York'
  });
};

// Scanner Panel Component
const ScannerPanel: React.FC<ScannerPanelProps> = ({
  scanner,
  results,
  columns,
  sort,
  onSymbolClick,
  onRefresh,
  isLoading
}) => {
  // Guard against undefined scanner
  if (!scanner) {
    return (
      <Card className="h-full flex flex-col bg-card/50 border-border/50">
        <CardContent className="flex-1 flex items-center justify-center">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const getTypeIcon = (type: ScannerType) => {
    switch (type) {
      case 'MomoHOD':
      case 'MomoUp2Min':
      case 'GapUp':
      case 'TopGainers':
        return <TrendingUp className="h-4 w-4 text-emerald-500" />;
      case 'MomoLOD':
      case 'MomoDown2Min':
      case 'GapDown':
      case 'TopLosers':
        return <TrendingDown className="h-4 w-4 text-red-400" />;
      case 'VolatilityHalt':
      case 'NewsHalt':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'VolumeSpike':
        return <Volume2 className="h-4 w-4 text-blue-500" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  return (
    <Card className="h-full flex flex-col glass">
      <CardHeader className="py-2 px-3 border-b border-border/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getTypeIcon(scanner.type)}
            <CardTitle className="text-sm font-semibold">{scanner.name}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] font-mono tabular-nums">
              {results.length}
            </Badge>
            <StrategySummaryPopover scannerId={scanner.id} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
              className="h-6 w-6 p-0"
            >
              <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-0 overflow-auto min-h-0">
        <DynamicScannerTable
          results={results}
          columns={columns}
          sort={sort}
          onSymbolClick={onSymbolClick}
        />
      </CardContent>
    </Card>
  );
};

// News Halt Scanner Panel
const NewsHaltPanel: React.FC<{ results: ScannerResult[] }> = ({ results }) => {
  return (
    <Card className="h-full flex flex-col glass">
      <CardHeader className="py-2 px-3 border-b border-border/30">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <CardTitle className="text-sm font-semibold">News Halts</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="grid grid-cols-5 gap-1 px-2 py-1 text-xs font-medium text-muted-foreground border-b border-border/30 bg-muted/30" style={{ minWidth: '320px' }}>
            <div>Name</div>
            <div>HaltDate</div>
            <div>HaltTime</div>
            <div>ResTime</div>
            <div>HaltPrice</div>
          </div>
          <div className="overflow-y-auto h-full">
            {results.filter(r => r.isHalted || r.haltTime).map((result, index) => (
              <div
                key={result.symbol}
                className={`grid grid-cols-5 gap-1 px-2 py-1 text-xs ${
                  index % 2 === 0 ? 'bg-background/30' : ''
                }`}
                style={{ minWidth: '320px' }}
              >
                <div className="font-medium text-foreground">{result.symbol}</div>
                <div className="text-muted-foreground">
                  {result.haltTime ? new Date(result.haltTime).toLocaleDateString() : '-'}
                </div>
                <div className="text-yellow-500">
                  {result.haltTime ? formatTime(result.haltTime) : '-'}
                </div>
                <div className="text-green-500">
                  {result.resTime ? formatTime(result.resTime) : '-'}
                </div>
                <div className="text-right">{formatPrice(result.price)}</div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Volatility Halt Scanner Panel  
const VolatilityHaltPanel: React.FC<{ 
  results: ScannerResult[];
  recentlyResumed?: ScannerResult[];
}> = ({ results, recentlyResumed = [] }) => {
  return (
    <Card className="h-full flex flex-col glass">
      <CardHeader className="py-2 px-3 border-b border-border/30">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-orange-500" />
          <CardTitle className="text-sm font-semibold">Halt Watch</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-0 overflow-hidden">
        <div className="overflow-x-auto">
          {/* Currently Halted */}
          <div className="border-b border-border/30">
            <div className="grid grid-cols-4 gap-1 px-2 py-1 text-xs font-medium text-muted-foreground bg-muted/30" style={{ minWidth: '280px' }}>
              <div>Name</div>
              <div>HaltTime</div>
              <div>ResTime</div>
              <div className="text-right">ResPrice</div>
            </div>
            {results.filter(r => r.isHalted).map((result, index) => (
              <div
                key={result.symbol}
                className={`grid grid-cols-4 gap-1 px-2 py-1 text-xs ${
                  index % 2 === 0 ? 'bg-background/30' : ''
                }`}
                style={{ minWidth: '280px' }}
              >
                <div className="font-medium text-orange-400">{result.symbol}</div>
                <div className="text-yellow-500">
                  {result.haltTime ? formatTime(result.haltTime) : '-'}
                </div>
                <div className="text-muted-foreground">-</div>
                <div className="text-right">{formatPrice(result.price)}</div>
              </div>
            ))}
          </div>

          {/* Pre-Halt (Limit State) */}
          <div className="border-b border-border/30">
            <div className="px-2 py-1 text-xs font-medium text-orange-400 bg-orange-900/20">
              Pre-Halt (Limit State)
            </div>
            <div className="grid grid-cols-2 gap-1 px-2 py-1 text-xs font-medium text-muted-foreground bg-muted/30" style={{ minWidth: '280px' }}>
              <div>Name</div>
              <div className="text-right">Dist%</div>
            </div>
          </div>

          {/* Recently Resumed */}
          <div>
            <div className="grid grid-cols-5 gap-1 px-2 py-1 text-xs font-medium text-muted-foreground bg-muted/30" style={{ minWidth: '320px' }}>
              <div>Name</div>
              <div>Reason</div>
              <div>Price</div>
              <div>Warn</div>
              <div>Time</div>
            </div>
            {recentlyResumed.map((result, index) => (
              <div
                key={result.symbol}
                className={`grid grid-cols-5 gap-1 px-2 py-1 text-xs ${
                  index % 2 === 0 ? 'bg-background/30' : ''
                }`}
                style={{ minWidth: '320px' }}
              >
                <div className="font-medium text-foreground">{result.symbol}</div>
                <div className={`${result.changePercent < 0 ? 'text-red-500' : 'text-green-500'} truncate`}>
                  {formatPercent(result.changePercent)}/2Min
                </div>
                <div>{formatPrice(result.price)}</div>
                <div className="text-yellow-500">
                  {Math.abs(result.changePercent) > 5 ? '⚠️' : ''}
                </div>
                <div className="text-muted-foreground">
                  {formatTime(result.detectedAt ?? result.lastUpdate)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Quick Symbol Add Buttons
const QuickSymbolButtons: React.FC<{
  symbols: string[];
  activeSymbol?: string;
  onSymbolClick: (symbol: string) => void;
}> = ({ symbols, activeSymbol, onSymbolClick }) => {
  const getSymbolColor = (symbol: string) => {
    // Color based on first letter for visual variety
    const colors = [
      'text-cyan-400',
      'text-green-400',
      'text-yellow-400',
      'text-orange-400',
      'text-pink-400',
      'text-purple-400'
    ];
    return colors[symbol.charCodeAt(0) % colors.length];
  };

  if (symbols.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 p-2 glass rounded-lg">
      {/* Unlabeled, this row read as decoration in the UX walkthrough. */}
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mr-1"
        title="Every symbol currently appearing in any panel below — click to open its chart & details">
        On scan
      </span>
      {symbols.map(symbol => (
        <Button
          key={symbol}
          variant={activeSymbol === symbol ? 'default' : 'ghost'}
          size="sm"
          onClick={() => onSymbolClick(symbol)}
          className={`font-bold text-xs h-7 ${activeSymbol !== symbol ? getSymbolColor(symbol) : ''}`}
        >
          {symbol}
        </Button>
      ))}
    </div>
  );
};

// Market Index Display
// Main Scanner Dashboard
export const ScannerDashboard: React.FC<{
  onSymbolSelect?: (symbol: string) => void;
}> = ({ onSymbolSelect }) => {
  // Scanner configurations — matches the 8 preset scanner IDs in the backend
  const scannerConfigs: ScannerConfig[] = [
    { id: 'momo-hod', name: 'HOD Break', description: 'Momentum High of Day', type: 'MomoHOD', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'gap-up', name: 'Gap Up', description: 'Pre-market Gap Up', type: 'GapUp', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'gap-down', name: 'Gap Down', description: 'Pre-market Gap Down', type: 'GapDown', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'momo-up-2min', name: '2-min Momo UP', description: '2-minute Momentum Up', type: 'MomoUp2Min', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'momo-down-2min', name: '2-min Momo DOWN', description: '2-minute Momentum Down', type: 'MomoDown2Min', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'volatility-halt', name: 'Volatility Halt', description: 'Stocks in volatility halt', type: 'VolatilityHalt', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'volume-spike', name: 'Volume Spike', description: 'Unusual volume activity', type: 'VolumeSpike', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'top-gainers', name: 'Top Gainers', description: 'Biggest percentage gainers', type: 'TopGainers', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    // Both are rendered below (News Halt panel, and the Halt Watch panel's recently-resumed list).
    // Without entries here nothing subscribes to them, so those panels stayed empty however many
    // rows the backend grains held.
    { id: 'news-halt', name: 'News Halt', description: 'Stocks halted for pending or released news', type: 'NewsHalt', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
    { id: 'halt-resume', name: 'Halt / Resume', description: 'Recently resumed after a halt', type: 'HaltResume', filters: [], columns: [], maxResults: 20, isActive: true, isPreset: true, createdAt: new Date().toISOString() },
  ];

  // Scanners that filter or sort on live-only data (halt state) cannot be replayed: the backtest
  // session REFUSES them outright, and EnableScannersAsync rethrows, so submitting the full list
  // would abort the whole run before the clock starts rather than just omitting a panel.
  const BACKTESTABLE_EXCLUDED = new Set(['volatility-halt', 'news-halt', 'halt-resume']);

  const [scanners] = useState<ScannerConfig[]>(scannerConfigs);
  const [scannerResults, setScannerResults] = useState<Record<string, ScannerResult[]>>({});
  const [scannerMeta, setScannerMeta] = useState<Record<string, ScannerMetadata>>({});
  const [activeSymbols, setActiveSymbols] = useState<string[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [connected, setConnected] = useState(false);

  // ── Backtest state ──────────────────────────────
  const [mode, setMode] = useState<'live' | 'backtest'>('live');
  const [backtestState, setBacktestState] = useState<BacktestState>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [clockTick, setClockTick] = useState<BacktestClockTick | null>(null);
  const [speed, setSpeed] = useState(1);
  const [strategyIds, setStrategyIds] = useState<string[]>([]);
  const [availableRules, setAvailableRules] = useState<Strategy[]>([]);
  const sessionIdRef = useRef<string | null>(null);

  // Load automation rules for the toggle
  useEffect(() => {
    automationService.getRules().then(setAvailableRules).catch(() => {});
  }, []);

  // Keep ref in sync
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // ── Live mode subscription ────────────────────
  useEffect(() => {
    if (mode !== 'live') return;
    let mounted = true;

    const setup = async () => {
      // Listen for one-time metadata (columns, sort, filters, description)
      signalRService.addScannerMetadataListener('scanner-dashboard', (scannerId: string, metadata: ScannerMetadata) => {
        if (!mounted) return;
        setScannerMeta(prev => ({ ...prev, [scannerId]: metadata }));
      });

      signalRService.addScannerUpdateListener('scanner-dashboard', (scannerId: string, update: any) => {
        if (!mounted) return;
        const results: ScannerResult[] = update.results ?? [];
        setScannerResults(prev => ({ ...prev, [scannerId]: results }));

        setScannerResults(latest => {
          const allSymbols = new Set<string>();
          Object.values(latest).forEach(r => r.forEach(s => allSymbols.add(s.symbol)));
          const arr = Array.from(allSymbols).slice(0, 15);
          setActiveSymbols(arr);
          if (!selectedSymbol && arr.length > 0) setSelectedSymbol(arr[0]);
          return latest;
        });
      });

      for (const scanner of scannerConfigs) {
        await signalRService.subscribeToScanner(scanner.id);
      }
      if (mounted) setConnected(true);
    };

    setup().catch(err => console.error('Scanner setup failed:', err));

    return () => {
      mounted = false;
      signalRService.removeScannerUpdateListener('scanner-dashboard');
      signalRService.removeScannerMetadataListener('scanner-dashboard');
      scannerConfigs.forEach(s => signalRService.unsubscribeFromScanner(s.id));
    };
  }, [mode]);

  // ── Backtest mode subscription ────────────────
  useEffect(() => {
    if (mode !== 'backtest') return;

    // Metadata listener for backtest scanners (sent on JoinBacktestScannerGroup)
    signalRService.addScannerMetadataListener('scanner-dashboard-bt', (scannerId: string, metadata: ScannerMetadata) => {
      setScannerMeta(prev => ({ ...prev, [scannerId]: metadata }));
    });

    // Scanner update listener — same event name ScannerUpdated from backtest groups
    signalRService.addScannerUpdateListener('scanner-dashboard-bt', (scannerId: string, update: any) => {
      const results: ScannerResult[] = update.results ?? [];
      setScannerResults(prev => ({ ...prev, [scannerId]: results }));

      setScannerResults(latest => {
        const allSymbols = new Set<string>();
        Object.values(latest).forEach(r => r.forEach(s => allSymbols.add(s.symbol)));
        const arr = Array.from(allSymbols).slice(0, 15);
        setActiveSymbols(arr);
        return latest;
      });
    });

    // Clock tick listener — also transitions creating→running when the first tick arrives
    signalRService.addBacktestClockListener('scanner-dashboard-clock', (tick: BacktestClockTick) => {
      setClockTick(tick);
      setBacktestState(prev => (prev === 'creating' ? 'running' : prev));
    });

    return () => {
      signalRService.removeScannerUpdateListener('scanner-dashboard-bt');
      signalRService.removeScannerMetadataListener('scanner-dashboard-bt');
      signalRService.removeBacktestClockListener('scanner-dashboard-clock');
    };
  }, [mode]);

  // ── Mode switch handler ───────────────────────
  const handleModeSwitch = useCallback(async (newMode: 'live' | 'backtest') => {
    if (newMode === mode) return;

    // Cleanup current backtest session if switching to live
    if (mode === 'backtest' && sessionIdRef.current) {
      try {
        await backtestService.stopSession(sessionIdRef.current);
        await signalRService.leaveAllBacktestScannerGroups(sessionIdRef.current);
        await signalRService.leaveBacktestClockGroup(sessionIdRef.current);
      } catch { /* ignore cleanup errors */ }
      setSessionId(null);
      setBacktestState('idle');
      setClockTick(null);
    }

    // Clear results when switching
    setScannerResults({});
    setScannerMeta({});
    setActiveSymbols([]);
    setMode(newMode);
    setConnected(newMode === 'live');
  }, [mode]);

  // ── Backtest controls ─────────────────────────
  const handleCreateAndStart = useCallback(async (startDate: string, endDate: string, startTime: string, endTime: string, startSpeed: number) => {
    setBacktestState('creating');
    setScannerResults({});
    setActiveSymbols([]);
    setClockTick(null);

    try {
      const scannerIds = scannerConfigs.map(s => s.id).filter(id => !BACKTESTABLE_EXCLUDED.has(id));

      // 1. Create session — backend auto-discovers symbols from dataset
      const { sessionId: sid } = await backtestService.createSession({
        startDate,
        endDate,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        symbols: [],
        playbackSpeed: startSpeed,
        dataType: 'minute',
        timeframe: '1M',
        scannerIds,
        // Local UI state name is `strategyIds` (the user picks rules in the
        // sidebar), but the backend wire field is `strategyRuleIds` —
        // distinct from `scannerStrategyIds` (analysis presets). Sending
        // the wrong key drops the selection silently and starts the
        // backtest without the user's auto-trading rules.
        strategyRuleIds: strategyIds.length > 0 ? strategyIds : undefined,
      });

      setSessionId(sid);
      sessionIdRef.current = sid;

      // 2. Join SignalR groups for each scanner + clock
      for (const scanner of scannerConfigs) {
        await signalRService.joinBacktestScannerGroup(sid, scanner.id);
      }
      await signalRService.joinBacktestClockGroup(sid);
      await signalRService.joinBacktestMarketIndicesGroup(sid);

      // 3. Start the session (returns immediately — backend initializes in background)
      //    State stays 'creating' until the first clock tick arrives via SignalR
      await backtestService.startSession(sid);
    } catch (err) {
      console.error('Failed to create/start backtest:', err);
      setBacktestState('idle');
    }
  }, []);

  const handlePause = useCallback(async () => {
    if (!sessionIdRef.current) return;
    try {
      await backtestService.pauseSession(sessionIdRef.current);
      setBacktestState('paused');
    } catch (err) {
      console.error('Failed to pause:', err);
    }
  }, []);

  const handleResume = useCallback(async () => {
    if (!sessionIdRef.current) return;
    try {
      await backtestService.resumeSession(sessionIdRef.current);
      setBacktestState('running');
    } catch (err) {
      console.error('Failed to resume:', err);
    }
  }, []);

  const handleStop = useCallback(async () => {
    if (!sessionIdRef.current) return;
    try {
      await backtestService.stopSession(sessionIdRef.current);
      await signalRService.leaveAllBacktestScannerGroups(sessionIdRef.current);
      await signalRService.leaveBacktestClockGroup(sessionIdRef.current);
      await signalRService.leaveBacktestMarketIndicesGroup(sessionIdRef.current);
    } catch { /* ignore */ }
    setSessionId(null);
    setBacktestState('idle');
    setClockTick(null);
  }, []);

  const handleSpeedChange = useCallback(async (newSpeed: number) => {
    setSpeed(newSpeed);
    if (sessionIdRef.current && (backtestState === 'running' || backtestState === 'paused')) {
      try {
        await backtestService.setSpeed(sessionIdRef.current, newSpeed);
      } catch (err) {
        console.error('Failed to set speed:', err);
      }
    }
  }, [backtestState]);

  const handleSymbolClick = (symbol: string) => {
    setSelectedSymbol(symbol);
    onSymbolSelect?.(symbol);
  };

  const getScanner = (id: string) => scanners.find(s => s.id === id);
  const getResults = (id: string) => scannerResults[id] || [];
  const getMeta = (id: string) => scannerMeta[id];

  return (
    <div className="h-full flex flex-col gap-3 p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <h1 className="text-lg font-bold">Scanner</h1>

          {/* Mode toggle */}
          <div className="flex items-center rounded-md border border-border/50 overflow-hidden">
            <Button
              variant={mode === 'live' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 px-2.5 text-xs rounded-none gap-1"
              onClick={() => handleModeSwitch('live')}
            >
              <Radio className="h-3 w-3" /> Live
            </Button>
            <Button
              variant={mode === 'backtest' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 px-2.5 text-xs rounded-none gap-1"
              onClick={() => handleModeSwitch('backtest')}
            >
              <History className="h-3 w-3" /> Backtest
            </Button>
          </div>

          {mode === 'live' && (
            <Badge
              variant={connected ? "default" : "secondary"}
              className="text-[10px]"
            >
              {connected ? "Live" : "Connecting..."}
            </Badge>
          )}
          {mode === 'backtest' && backtestState === 'running' && (
            <Badge variant="default" className="text-[10px] bg-amber-600">
              Backtest Running
            </Badge>
          )}
          {mode === 'backtest' && backtestState === 'paused' && (
            <Badge variant="secondary" className="text-[10px]">
              Backtest Paused
            </Badge>
          )}
        </div>
      </div>

      {/* Backtest control bar (only in backtest mode) */}
      {mode === 'backtest' && (
        <>
          <BacktestControlBar
            state={backtestState}
            clockTick={clockTick}
            speed={speed}
            onCreateAndStart={handleCreateAndStart}
            onPause={handlePause}
            onResume={handleResume}
            onStop={handleStop}
            onSpeedChange={handleSpeedChange}
          />
          {/* Automation rules toggle */}
          {availableRules.length > 0 && (backtestState === 'idle' || backtestState === 'stopped') && (
            <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-lg border border-border/40">
              <Bot className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Auto-trade:</span>
              {availableRules.map(r => (
                <label key={r.id} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border"
                    checked={strategyIds.includes(r.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setStrategyIds(prev => [...prev, r.id]);
                      } else {
                        setStrategyIds(prev => prev.filter(id => id !== r.id));
                      }
                    }}
                  />
                  <span className="text-xs">{r.name}</span>
                </label>
              ))}
            </div>
          )}
        </>
      )}

      {/* First-run tour — dismissible, landing tab only */}
      {mode === 'live' && <FirstRunChecklist />}

      {/* Quick Symbol Buttons */}
      <QuickSymbolButtons
        symbols={activeSymbols}
        activeSymbol={selectedSymbol}
        onSymbolClick={handleSymbolClick}
      />

      {/* Scanner Grid - Trade Ideas Style Layout */}
      {scanners.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading scanners...</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 min-h-0 auto-rows-[minmax(250px,1fr)]">
          {/* Row 1: Main Scanners */}
          {getScanner('momo-hod') && (
            <ScannerPanel
              scanner={getScanner('momo-hod')!}
              results={getResults('momo-hod')}
              columns={getMeta('momo-hod')?.columns}
              sort={getMeta('momo-hod')?.sort}
              onSymbolClick={handleSymbolClick}
            />
          )}
          {getScanner('gap-up') && (
            <ScannerPanel
              scanner={getScanner('gap-up')!}
              results={getResults('gap-up')}
              columns={getMeta('gap-up')?.columns}
              sort={getMeta('gap-up')?.sort}
              onSymbolClick={handleSymbolClick}
            />
          )}
          {getScanner('momo-up-2min') && (
            <ScannerPanel
              scanner={getScanner('momo-up-2min')!}
              results={getResults('momo-up-2min')}
              columns={getMeta('momo-up-2min')?.columns}
              sort={getMeta('momo-up-2min')?.sort}
              onSymbolClick={handleSymbolClick}
            />
          )}
          {/* recentlyResumed was fed 2-minute momentum rows — unrelated symbols shown as
              "recently resumed". halt-resume now genuinely tracks resumed symbols
              (MinutesSinceResume), which is only possible since halt state became real. */}
          <VolatilityHaltPanel
            results={getResults('volatility-halt')}
            recentlyResumed={getResults('halt-resume').slice(0, 5)}
          />

          {/* Row 2: Additional Scanners */}
          {getScanner('top-gainers') && (
            <ScannerPanel
              scanner={getScanner('top-gainers')!}
              results={getResults('top-gainers')}
              columns={getMeta('top-gainers')?.columns}
              sort={getMeta('top-gainers')?.sort}
              onSymbolClick={handleSymbolClick}
            />
          )}
          {getScanner('momo-down-2min') && (
            <ScannerPanel
              scanner={getScanner('momo-down-2min')!}
              results={getResults('momo-down-2min')}
              columns={getMeta('momo-down-2min')?.columns}
              sort={getMeta('momo-down-2min')?.sort}
              onSymbolClick={handleSymbolClick}
            />
          )}
          {/* Its own scanner now. It used to borrow the volatility-halt rows, so a volatility
              pause showed up as a news halt and vice versa — the backend could not tell them
              apart until HaltKind was ingested. */}
          <NewsHaltPanel results={getResults('news-halt')} />
          {getScanner('volume-spike') && (
            <ScannerPanel
              scanner={getScanner('volume-spike')!}
              results={getResults('volume-spike')}
              columns={getMeta('volume-spike')?.columns}
              sort={getMeta('volume-spike')?.sort}
              onSymbolClick={handleSymbolClick}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ScannerDashboard;
